import { useRef, useState } from "react";
import { ModelBrowserOptions } from "./types";
import { browseAdvertOptions } from "./service";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import { message } from "antd";

export default function useAdvertOptions() {
  const [loadingOptionsFields, setLoadingOptionsFields] = useState<Set<keyof ModelBrowserOptions | 'searchDate'>>(new Set())
  const reqIdRef = useRef(0);
  const [options, setOptions] = useState<ModelBrowserOptions>({
    searchDate: "",
    type: [
      { value: "1", label: "Personbil" },
      { value: "2", label: "Varebil" },
      { value: "3", label: "Motorcykel" },
    ],
    manufacturer: [],
    model: [],
    variant: [],
  })


  const stopLoadingKey = (nextKey: HierarcyKey) => {
    setLoadingOptionsFields(prev => {
      const s = new Set(prev)
      s.delete(nextKey)
      return s
    })
  }


  async function handleDrillDown<T extends Partial<Record<HierarcyKey | "searchDate", string | undefined>>>(values: T, key: keyof T) {
    let changedKey = key === 'searchDate' ? "type" : key
    if (!changedKey || !isHierarchyKey(changedKey)) return;
    const nextKey = getNextKey(changedKey)
    if (nextKey) setLoadingOptionsFields(prev => new Set(prev).add(nextKey));

    const params: Parameters<typeof browseAdvertOptions>[0] = {
      searchDate: values.searchDate,
      type: values.type,
      manufacturer: values.manufacturer,
      model: values.model,
      variant: values.variant,
    } as const;

    if (!params.type) {
      if (nextKey) stopLoadingKey(nextKey)
      return;
    }

    const reqId = ++reqIdRef.current;
    try {
      const cleanedParams = clearDependentFields(params, changedKey, undefined)

      if (reqId !== reqIdRef.current) return;
      const response = await browseAdvertOptions(cleanedParams)
      if (nextKey) {
        if (response.status === 202) {
          if ('message' in response.data.data) message.loading(response.data.data.message)
          setOptions(prev => clearDependentFields(prev, 'type', []));
        } else {
          // update UI options
          setOptions(prev => {
            const cleaned = clearDependentFields(prev, changedKey, []);
            return {
              ...cleaned,
              [nextKey]: response.data.data,
            };
          });
        }
      }
    } catch (error) {
      console.error(error)
      renderErrorMessage(error)
    } finally {
      if (nextKey) stopLoadingKey(nextKey)
    }
  }


  return {
    options,
    loadingOptionsFields,
    handleDrillDown
  }
}

const getNextKey = (k: HierarcyKey): HierarcyKey | null => {
  const i = hierarchy.indexOf(k);
  return i >= 0 && i < hierarchy.length - 1 ? hierarchy[i + 1] : null;
};

type HierarcyKey = "type" | "manufacturer" | "model" | "variant"
const hierarchy: HierarcyKey[] = ['type', 'manufacturer', 'model', 'variant']

const isHierarchyKey = (val: unknown): val is HierarcyKey => hierarchy.includes(val as HierarcyKey);
export function clearDependentFields<
  T extends Record<string, unknown>,
  CV = undefined
>(
  obj: T,
  changedKey?: keyof T,
  clearValue: CV = undefined as CV
): T {
  if (!changedKey) return obj;

  if (!isHierarchyKey(changedKey)) {
    return obj; // nothing to clear
  }

  const idx = hierarchy.indexOf(changedKey);
  const toClear = hierarchy.slice(idx + 1);

  const result = { ...obj } as T & Partial<Record<HierarcyKey, CV>>;

  for (const key of toClear) {
    if (key in result) {
      result[key] = clearValue;
    }
  }

  return result as T;
}

